import React, { useState, useEffect } from 'react';
import { Mail, Send, Users, Check, X, Clock, Calendar, Search, Plus, Trash2, Download, Filter } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const InvitationsTab = ({ token }) => {
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showSendModal, setShowSendModal] = useState(false);
  
  // Send form state
  const [sendEventId, setSendEventId] = useState('');
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [customRecipients, setCustomRecipients] = useState([{ name: '', email: '', whatsapp: '' }]);
  const [message, setMessage] = useState('');

  const statusOptions = [
    { value: 'pending', label: 'Pendiente', color: 'bg-yellow-500/20 text-yellow-400', icon: Clock },
    { value: 'sent', label: 'Enviada', color: 'bg-blue-500/20 text-blue-400', icon: Send },
    { value: 'confirmed', label: 'Confirmada', color: 'bg-green-500/20 text-green-400', icon: Check },
    { value: 'declined', label: 'Declinada', color: 'bg-red-500/20 text-red-400', icon: X }
  ];

  useEffect(() => {
    fetchInvitations();
    fetchEvents();
    fetchCustomers();
  }, [selectedEvent, statusFilter]);

  const fetchInvitations = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (selectedEvent) params.append('event_id', selectedEvent);
      if (statusFilter) params.append('status', statusFilter);
      
      const response = await axios.get(`${API}/api/admin/invitations?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInvitations(response.data.items || []);
    } catch (error) {
      toast.error('Error al cargar invitaciones');
    } finally {
      setLoading(false);
    }
  };

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/api/admin/events?status=published`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEvents(response.data.items || []);
    } catch (error) {
      console.error('Error fetching events');
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/api/admin/customers?limit=500`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomers(response.data.items || []);
    } catch (error) {
      console.error('Error fetching customers');
    }
  };

  const handleSendInvitations = async () => {
    if (!sendEventId) {
      toast.error('Selecciona un evento');
      return;
    }
    
    if (selectedCustomers.length === 0 && customRecipients.every(r => !r.email)) {
      toast.error('Agrega al menos un destinatario');
      return;
    }

    try {
      const response = await axios.post(`${API}/api/admin/invitations/send`, {
        event_id: sendEventId,
        customer_ids: selectedCustomers,
        custom_recipients: customRecipients.filter(r => r.email),
        message: message
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`${response.data.invitations_created} invitaciones creadas`);
      setShowSendModal(false);
      resetSendForm();
      fetchInvitations();
    } catch (error) {
      toast.error('Error al enviar invitaciones');
    }
  };

  const handleUpdateStatus = async (invitationId, newStatus) => {
    try {
      await axios.put(`${API}/api/admin/invitations/${invitationId}`, 
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Estado actualizado');
      fetchInvitations();
    } catch (error) {
      toast.error('Error al actualizar');
    }
  };

  const handleDelete = async (invitationId) => {
    if (!window.confirm('¿Eliminar esta invitación?')) return;
    
    try {
      await axios.delete(`${API}/api/admin/invitations/${invitationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Invitación eliminada');
      fetchInvitations();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const resetSendForm = () => {
    setSendEventId('');
    setSelectedCustomers([]);
    setCustomRecipients([{ name: '', email: '', whatsapp: '' }]);
    setMessage('');
  };

  const addCustomRecipient = () => {
    setCustomRecipients([...customRecipients, { name: '', email: '', whatsapp: '' }]);
  };

  const updateCustomRecipient = (index, field, value) => {
    setCustomRecipients(prev => prev.map((r, i) => 
      i === index ? { ...r, [field]: value } : r
    ));
  };

  const removeCustomRecipient = (index) => {
    if (customRecipients.length > 1) {
      setCustomRecipients(prev => prev.filter((_, i) => i !== index));
    }
  };

  const toggleCustomer = (customerId) => {
    setSelectedCustomers(prev => 
      prev.includes(customerId) 
        ? prev.filter(id => id !== customerId)
        : [...prev, customerId]
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('es-MX', {
      day: 'numeric', month: 'short', year: 'numeric'
    });
  };

  const getStatusInfo = (status) => {
    return statusOptions.find(s => s.value === status) || statusOptions[0];
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center">
            <Mail className="w-6 h-6 mr-2 text-amber-500" />
            Invitaciones
          </h2>
          <p className="text-gray-400 text-sm">Gestiona invitaciones a eventos</p>
        </div>
        <button
          onClick={() => setShowSendModal(true)}
          className="flex items-center px-4 py-2 bg-amber-500 text-black font-medium rounded-lg hover:bg-amber-400"
        >
          <Send className="w-4 h-4 mr-2" />
          Nueva Invitación
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <select
          value={selectedEvent}
          onChange={(e) => setSelectedEvent(e.target.value)}
          className="bg-zinc-800 text-white px-4 py-2 rounded-lg border border-zinc-700"
        >
          <option value="">Todos los eventos</option>
          {events.map(event => (
            <option key={event.event_id} value={event.event_id}>{event.name?.es}</option>
          ))}
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="bg-zinc-800 text-white px-4 py-2 rounded-lg border border-zinc-700"
        >
          <option value="">Todos los estados</option>
          {statusOptions.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-zinc-900 rounded-xl p-4 border border-zinc-800">
          <p className="text-2xl font-bold text-white">{invitations.length}</p>
          <p className="text-gray-400 text-sm">Total</p>
        </div>
        <div className="bg-zinc-900 rounded-xl p-4 border border-zinc-800">
          <p className="text-2xl font-bold text-yellow-400">
            {invitations.filter(i => i.status === 'pending').length}
          </p>
          <p className="text-gray-400 text-sm">Pendientes</p>
        </div>
        <div className="bg-zinc-900 rounded-xl p-4 border border-zinc-800">
          <p className="text-2xl font-bold text-green-400">
            {invitations.filter(i => i.status === 'confirmed').length}
          </p>
          <p className="text-gray-400 text-sm">Confirmadas</p>
        </div>
        <div className="bg-zinc-900 rounded-xl p-4 border border-zinc-800">
          <p className="text-2xl font-bold text-red-400">
            {invitations.filter(i => i.status === 'declined').length}
          </p>
          <p className="text-gray-400 text-sm">Declinadas</p>
        </div>
      </div>

      {/* Invitations List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-amber-500 mx-auto"></div>
        </div>
      ) : invitations.length === 0 ? (
        <div className="text-center py-12 bg-zinc-900 rounded-xl">
          <Mail className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
          <p className="text-gray-400">No hay invitaciones</p>
        </div>
      ) : (
        <div className="bg-zinc-900 rounded-xl border border-zinc-800 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-zinc-800">
                  <th className="text-left p-4 text-gray-400 font-medium text-sm">Destinatario</th>
                  <th className="text-left p-4 text-gray-400 font-medium text-sm">Evento</th>
                  <th className="text-left p-4 text-gray-400 font-medium text-sm">Estado</th>
                  <th className="text-left p-4 text-gray-400 font-medium text-sm">Fecha</th>
                  <th className="text-left p-4 text-gray-400 font-medium text-sm">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {invitations.map((inv) => {
                  const statusInfo = getStatusInfo(inv.status);
                  const StatusIcon = statusInfo.icon;
                  
                  return (
                    <tr key={inv.invitation_id} className="border-b border-zinc-800 hover:bg-zinc-800/50">
                      <td className="p-4">
                        <p className="text-white">{inv.recipient_name}</p>
                        <p className="text-gray-500 text-sm">{inv.recipient_email}</p>
                      </td>
                      <td className="p-4">
                        <p className="text-gray-300">{inv.event_name}</p>
                      </td>
                      <td className="p-4">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs ${statusInfo.color}`}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {statusInfo.label}
                        </span>
                      </td>
                      <td className="p-4">
                        <p className="text-gray-400 text-sm">{formatDate(inv.created_at)}</p>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          {inv.status === 'pending' && (
                            <>
                              <button
                                onClick={() => handleUpdateStatus(inv.invitation_id, 'sent')}
                                className="p-2 text-blue-400 hover:bg-blue-500/10 rounded"
                                title="Marcar como enviada"
                              >
                                <Send className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleUpdateStatus(inv.invitation_id, 'confirmed')}
                                className="p-2 text-green-400 hover:bg-green-500/10 rounded"
                                title="Marcar como confirmada"
                              >
                                <Check className="w-4 h-4" />
                              </button>
                            </>
                          )}
                          {inv.status === 'sent' && (
                            <>
                              <button
                                onClick={() => handleUpdateStatus(inv.invitation_id, 'confirmed')}
                                className="p-2 text-green-400 hover:bg-green-500/10 rounded"
                                title="Confirmar"
                              >
                                <Check className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleUpdateStatus(inv.invitation_id, 'declined')}
                                className="p-2 text-red-400 hover:bg-red-500/10 rounded"
                                title="Declinar"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </>
                          )}
                          <button
                            onClick={() => handleDelete(inv.invitation_id)}
                            className="p-2 text-gray-400 hover:text-red-500"
                            title="Eliminar"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Send Modal */}
      {showSendModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setShowSendModal(false)}>
          <div className="bg-zinc-900 rounded-2xl max-w-3xl w-full border border-zinc-800 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="p-6 border-b border-zinc-800 flex items-center justify-between sticky top-0 bg-zinc-900">
              <h3 className="text-xl font-bold text-white">Enviar Invitaciones</h3>
              <button onClick={() => setShowSendModal(false)} className="text-gray-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Event Selection */}
              <div>
                <label className="block text-gray-400 text-sm mb-2">Evento *</label>
                <select
                  value={sendEventId}
                  onChange={(e) => setSendEventId(e.target.value)}
                  className="w-full bg-zinc-800 text-white px-4 py-3 rounded-lg border border-zinc-700 focus:border-amber-500 focus:outline-none"
                >
                  <option value="">Seleccionar evento...</option>
                  {events.map(event => (
                    <option key={event.event_id} value={event.event_id}>
                      {event.name?.es} - {event.date}
                    </option>
                  ))}
                </select>
              </div>

              {/* Existing Customers */}
              {customers.length > 0 && (
                <div>
                  <label className="block text-gray-400 text-sm mb-2">
                    Clientes existentes ({selectedCustomers.length} seleccionados)
                  </label>
                  <div className="bg-zinc-800 rounded-lg max-h-48 overflow-y-auto p-2">
                    {customers.map(customer => (
                      <label key={customer.customer_id} className="flex items-center p-2 hover:bg-zinc-700 rounded cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedCustomers.includes(customer.customer_id)}
                          onChange={() => toggleCustomer(customer.customer_id)}
                          className="mr-3"
                        />
                        <div>
                          <p className="text-white text-sm">{customer.name}</p>
                          <p className="text-gray-500 text-xs">{customer.email}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Custom Recipients */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-gray-400 text-sm">Agregar destinatarios manualmente</label>
                  <button
                    type="button"
                    onClick={addCustomRecipient}
                    className="text-amber-500 text-sm hover:text-amber-400"
                  >
                    + Agregar
                  </button>
                </div>
                <div className="space-y-2">
                  {customRecipients.map((recipient, index) => (
                    <div key={index} className="flex gap-2">
                      <input
                        type="text"
                        value={recipient.name}
                        onChange={(e) => updateCustomRecipient(index, 'name', e.target.value)}
                        placeholder="Nombre"
                        className="flex-1 bg-zinc-800 text-white px-3 py-2 rounded-lg border border-zinc-700 focus:border-amber-500 focus:outline-none text-sm"
                      />
                      <input
                        type="email"
                        value={recipient.email}
                        onChange={(e) => updateCustomRecipient(index, 'email', e.target.value)}
                        placeholder="Email *"
                        className="flex-1 bg-zinc-800 text-white px-3 py-2 rounded-lg border border-zinc-700 focus:border-amber-500 focus:outline-none text-sm"
                      />
                      <input
                        type="tel"
                        value={recipient.whatsapp}
                        onChange={(e) => updateCustomRecipient(index, 'whatsapp', e.target.value)}
                        placeholder="WhatsApp"
                        className="w-32 bg-zinc-800 text-white px-3 py-2 rounded-lg border border-zinc-700 focus:border-amber-500 focus:outline-none text-sm"
                      />
                      {customRecipients.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeCustomRecipient(index)}
                          className="p-2 text-red-500 hover:bg-red-500/10 rounded"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Message */}
              <div>
                <label className="block text-gray-400 text-sm mb-2">Mensaje personalizado (opcional)</label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={3}
                  placeholder="Mensaje adicional para la invitación..."
                  className="w-full bg-zinc-800 text-white px-4 py-3 rounded-lg border border-zinc-700 focus:border-amber-500 focus:outline-none resize-none"
                />
              </div>
            </div>

            <div className="p-6 border-t border-zinc-800 flex justify-end gap-3">
              <button
                onClick={() => setShowSendModal(false)}
                className="px-4 py-2 bg-zinc-800 text-white rounded-lg hover:bg-zinc-700"
              >
                Cancelar
              </button>
              <button
                onClick={handleSendInvitations}
                className="px-4 py-2 bg-amber-500 text-black font-medium rounded-lg hover:bg-amber-400 flex items-center"
              >
                <Send className="w-4 h-4 mr-2" />
                Crear Invitaciones
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvitationsTab;
